import { ChangeEvent, useContext } from 'react';
import React from 'react';

import {
    Button,
    ChevronDownIcon,
    Controller,
    H2,
    RadioButtonGroup,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';

import { ApplicationContext } from '@src/app/contexts/Application';
import { getFormHelpers, radioButtonOptions } from '@src/shared/helpers/input';
import CallOut from '@src/ui/components/CallOut/Callout';
import { CertifyDetailsCallout } from '@src/ui/components/CertifyDetailsCallout/CertifyDetailsCallout';
import ErrorContainer from '@src/ui/components/ErrorContainer/ErrorContainer';
import { FormError } from '@src/ui/components/FormError';
import { HttpError } from '@src/ui/components/HttpError';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';

interface IOutgoingsViewFormProps {
    formikProps: any;
    formItems: any;
    errors: {};
    httpError: string | null;
    coApplicantName?: string;
}

const OutgoingsViewForm = ({
    formikProps,
    formItems,
    errors,
    httpError,
    coApplicantName,
}: IOutgoingsViewFormProps) => {
    const { propertyDetails } = useContext(ApplicationContext);

    const handleRadioChange = (e: any) => {
        formikProps.setTouched({
            ...formikProps.touched,
            haveDependants: true,
        });

        const value = e.target.value === 'true';
        if (value !== formikProps.values.haveDependants) {
            [
                'adultDependants',
                'childDependants',
                'educationAndChildcare',
                'alimony',
            ].forEach((field) => formikProps.setFieldValue(field, null));
        }

        formikProps.setFieldValue('haveDependants', value);
    };

    const handleNumericChange = (fieldName: string) => {
        return (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value;
            const validValue = value.replace(/[^\d]/g, '');

            formikProps.setFieldValue(fieldName, validValue);
        };
    };

    const { getError } = getFormHelpers(formItems, formikProps);

    return (
        <>
            <div className='grid grid-cols-1 mt-6 mb-6 gap-8'>
                <Controller
                    label={`Does anyone rely on you ${
                        coApplicantName ? `or ${coApplicantName}` : ''
                    } financially?`}
                    additionalInfo={
                        'For example, a partner, children or relatives'
                    }
                >
                    <RadioButtonGroup
                        name='haveDependants'
                        id='haveDependants'
                        value={formikProps.values.haveDependants?.toString()}
                        error={{
                            hasError: false,
                            errorMessage: getError('haveDependants'),
                        }}
                        onChange={handleRadioChange}
                        options={radioButtonOptions}
                    />
                </Controller>

                {formikProps.values.haveDependants && (
                    <div className='grid grid-cols-1 gap-4 bg-almond-500 p-4 rounded-lg'>
                        <SelectBox
                            items={formItems.haveDependants.items}
                            suffix={<ChevronDownIcon />}
                            label='How many under 18?'
                            name='childDependants'
                            validState={!!formikProps.values.childDependants}
                            error={getError('childDependants')}
                            value={formikProps.values.childDependants || ' '}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                formikProps.setFieldValue(
                                    'childDependants',
                                    e.currentTarget.value,
                                );
                            }}
                            onBlur={() => {
                                return formikProps.setTouched({
                                    ...formikProps.touched,
                                    childDependants: true,
                                });
                            }}
                        />

                        <SelectBox
                            items={formItems.haveDependants.items}
                            suffix={<ChevronDownIcon />}
                            label='How many over 18?'
                            name='adultDependants'
                            validState={!!formikProps.values.adultDependants}
                            error={getError('adultDependants')}
                            value={formikProps.values.adultDependants || ' '}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                formikProps.setFieldValue(
                                    'adultDependants',
                                    e.currentTarget.value,
                                );
                            }}
                            onBlur={() => {
                                return formikProps.setTouched({
                                    ...formikProps.touched,
                                    adultDependants: true,
                                });
                            }}
                        />
                    </div>
                )}

                <H2 className='-mb-4'>
                    How much do you{' '}
                    {coApplicantName ? `and ${coApplicantName}` : ''} spend
                    monthly on...
                </H2>

                {formikProps.values.haveDependants && (
                    <TextField
                        prefix={<StirlingIcon />}
                        label='School, nursery and childcare fees'
                        maxLength={7}
                        mask={'number' as any}
                        name='educationAndChildcare'
                        value={
                            formikProps.values.educationAndChildcare?.toString() ||
                            ''
                        }
                        onChange={handleNumericChange('educationAndChildcare')}
                        onBlur={formikProps.handleBlur}
                        error={getError('educationAndChildcare')}
                    />
                )}

                {formikProps.values.haveDependants && (
                    <TextField
                        prefix={<StirlingIcon />}
                        label='Spousal and child maintenance'
                        maxLength={7}
                        mask={'number' as any}
                        name='alimony'
                        value={formikProps.values.alimony?.toString() || ''}
                        onChange={handleNumericChange('alimony')}
                        onBlur={formikProps.handleBlur}
                        error={getError('alimony')}
                    />
                )}

                <TextField
                    prefix={<StirlingIcon />}
                    label='Council tax'
                    maxLength={7}
                    mask={'currency' as any}
                    name='councilTax'
                    value={formikProps.values.councilTax?.toString() || ''}
                    onChange={handleNumericChange('councilTax')}
                    onBlur={formikProps.handleBlur}
                    error={getError('councilTax')}
                />

                {propertyDetails.yearsRemainingOnLease && (
                    <TextField
                        prefix={<StirlingIcon />}
                        label='Ground rent and service charge'
                        maxLength={7}
                        mask={'number' as any}
                        name='rent'
                        value={formikProps.values.rent?.toString() || ''}
                        onChange={handleNumericChange('rent')}
                        onBlur={formikProps.handleBlur}
                        error={getError('rent')}
                    />
                )}

                <CallOut classes='items-center'>
                    <div className='text-sm_body leading-120%'>
                        This covers everything - you don’t need to tell us about
                        other spending
                    </div>
                </CallOut>

                <CertifyDetailsCallout />
            </div>

            {httpError && <HttpError error={httpError} />}
            <ErrorContainer formikProps={formikProps} />
            <FormError otherErrors={errors} />

            <div className='flex gap-5 justify-end mt-12'>
                <Button
                    color='secondary'
                    type='submit'
                    size='large'
                    className='w-full'
                >
                    Continue
                </Button>
            </div>
        </>
    );
};

export default OutgoingsViewForm;
